import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CAMINHO_DO_LOGIN } from '@/componentes/Casca';
import { CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { CoMarcaLogin } from '@/componentes/Marca';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora } from '@/lib/formato';
import { comCorpo, explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { primeiraMaiuscula } from '@/lib/texto';
import { codigoDoFragmento } from '@/modulos/cadastros/convite';
import {
  AJUDA_DA_SENHA,
  COMPRIMENTO_MAXIMO_DA_SENHA,
  problemaObvioNaSenha,
  validarRepeticao,
  validarSenhaNoCampo,
} from '@/lib/senha';

/**
 * O convite de primeiro acesso (migração 0024): a pessoa escolhe a própria
 * senha e entra. Serve também para trocar a senha perdida, quando quem
 * administra emite o convite para quem já entrava.
 *
 * Como o login, roda fora da casca e sem sessão. O código vem do FRAGMENTO do
 * endereço, que o navegador não envia a servidor nenhum, e segue no corpo de
 * um POST — nunca no caminho, que entra em log de acesso.
 */

interface ConviteAberto {
  readonly nome: string;
  readonly email: string;
  readonly empresa: string;
  readonly expiraEm: string;
  readonly jaTemAcesso: boolean;
}

interface Aceite {
  readonly aceito: true;
  readonly entrou: boolean;
  readonly email: string;
}

/**
 * O convite aberto NO CELULAR vai para o app (decisao 49, M02): o link que
 * quem administra manda e o mesmo, e quem o toca no telefone cai na tela
 * feita para o telefone. O codigo segue no fragmento, que nao sai do aparelho.
 */
function abertoNoCelular(): boolean {
  return /iPhone|iPod|Android.*Mobile/i.test(window.navigator.userAgent);
}

export function PaginaPrimeiroAcesso() {
  const noCelular = useMemo(() => abertoNoCelular(), []);
  useEffect(() => {
    if (noCelular) window.location.replace(`/m/primeiro-acesso${window.location.hash}`);
  }, [noCelular]);
  const codigo = useMemo(() => codigoDoFragmento(window.location.hash), []);
  if (noCelular) return null;

  return (
    <div className="h-full overflow-auto bg-tela">
      <div className="mx-auto flex max-w-[688px] flex-col gap-8 p-[var(--margem-xl)] max-[1280px]:p-[var(--margem-m)] max-[768px]:p-[var(--margem-xs)]">
        <CoMarcaLogin />
        {codigo === null ? (
          <Recusa
            erro="o endereço do convite está incompleto"
            comoResolver="Confira se o endereço foi copiado inteiro, com a parte depois do #. Se estiver inteiro, peça um convite novo a quem administra o sistema."
          />
        ) : (
          <Convite codigo={codigo} />
        )}
      </div>
    </div>
  );
}

function Convite({ codigo }: { readonly codigo: string }) {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [senha, setSenha] = useState('');
  const [repeticao, setRepeticao] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  const [gravadaSemEntrar, setGravadaSemEntrar] = useState<string | null>(null);

  const convite = useQuery({
    queryKey: ['convite', codigo],
    queryFn: () => comCorpo<ConviteAberto>('POST', '/convite/consulta', { codigo }),
    retry: false,
    staleTime: Infinity,
  });

  const aceitar = useMutation({
    mutationFn: () => comCorpo<Aceite>('POST', '/convite/aceite', { codigo, senha }),
    meta: {
      retorno: retornoDe<Aceite>((a) => ({
        titulo: 'Senha criada',
        alvo: { nome: a.email },
        efeito: a.entrou ? 'Você já entrou no sistema' : 'Entre com o e-mail e a senha nova',
      })),
    },
    onSuccess: (aceite) => {
      setSenha('');
      setRepeticao('');
      if (!aceite.entrou) {
        setGravadaSemEntrar(aceite.email);
        return;
      }
      /* A sessão é nova e de outra pessoa, talvez: o cache inteiro sai, como
         no login. */
      cliente.clear();
      void navegar({ to: '/painel' });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const obvio = problemaObvioNaSenha(senha);
    if (obvio !== null) {
      setProblema(obvio);
      return;
    }
    if (senha !== repeticao) {
      setProblema('As duas senhas não são iguais. Digite a mesma senha nos dois campos.');
      return;
    }
    setProblema(null);
    aceitar.mutate();
  }

  /* Conferindo o convite (p.32): a forma da tela que vem, com o que já se sabe
     escrito — o sobretítulo, o título do bloco e o rótulo dos dois campos. O
     título e o subtítulo dependem do convite, e viram barra. */
  if (convite.isPending) {
    return (
      <>
        <Carregando oQue="Conferindo o convite" className="flex min-w-0 flex-col gap-1">
          <span aria-hidden="true" className="txt-sobretitulo text-tinta-apagada">
            CT Frotas
          </span>
          <Barra className="my-1 h-7 w-48" />
          <Barra className="my-1 h-3 w-3/4" />
        </Carregando>
        <div aria-hidden="true">
          <Bloco titulo="Escolha a sua senha">
            <div className="flex flex-col">
              <Barra className="my-1 h-3 w-full" />
              <Barra className="my-1 h-3 w-3/5" />
            </div>
            <div className="flex flex-col gap-4">
              <span className="flex flex-col gap-2">
                <span className="txt-rotulo-campo">Senha</span>
                <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
                <span className="txt-ajuda">{`${AJUDA_DA_SENHA} Quem administra o sistema não fica sabendo a senha.`}</span>
              </span>
              <span className="flex flex-col gap-2">
                <span className="txt-rotulo-campo">Repita a senha</span>
                <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
              </span>
              <Barra className="ml-auto h-[var(--controle-m)] w-32 rounded-controle" />
            </div>
          </Bloco>
        </div>
      </>
    );
  }
  if (convite.isError) {
    const falha = explicarFalha(convite.error);
    return <Recusa erro={falha.erro} comoResolver={falha.comoResolver} />;
  }

  const dados = convite.data;
  const recusa = aceitar.isError ? explicarFalha(aceitar.error) : null;

  if (gravadaSemEntrar !== null) {
    return (
      <Bloco titulo="Senha gravada">
        <p className="txt-corpo text-tinta">{`Entre com ${gravadaSemEntrar} e a senha que você acabou de escolher.`}</p>
        <div className="flex justify-end">
          <Link to={CAMINHO_DO_LOGIN} className="botao botao-primario">
            Voltar à entrada
          </Link>
        </div>
      </Bloco>
    );
  }

  return (
    <>
      <HistoriaNaMesa codigo="TEL-ACS-02">
        <CabecalhoPagina
          sobretitulo="CT Frotas"
          titulo={dados.jaTemAcesso ? 'Trocar senha' : 'Primeiro acesso'}
          subtitulo={`Convite para ${dados.nome}, da ${dados.empresa}`}
        />
      </HistoriaNaMesa>

      <Bloco titulo="Escolha a sua senha">
        <p className="txt-apoio text-tinta-secundaria">
          {`Você vai entrar com ${dados.email}. Este convite vale uma vez e até ${formatarDataHora(new Date(dados.expiraEm))}.`}
          {dados.jaTemAcesso ? ' A senha anterior deixa de valer, e as sessões abertas com ela são encerradas.' : ''}
        </p>
        <form className="flex flex-col gap-4" onSubmit={enviar}>
          {/* O e-mail vai escondido no formulário para o gerenciador de
              senhas do navegador guardar a senha nova junto do usuário certo. */}
          <input type="email" autoComplete="username" value={dados.email} readOnly hidden />
          <CampoTexto
            rotulo="Senha"
            valor={senha}
            tipo="senha"
            autoComplete="new-password"
            maxLength={COMPRIMENTO_MAXIMO_DA_SENHA}
            ajuda={`${AJUDA_DA_SENHA} Quem administra o sistema não fica sabendo a senha.`}
            aoMudar={setSenha}
            validar={validarSenhaNoCampo}
          />
          <CampoTexto
            rotulo="Repita a senha"
            valor={repeticao}
            tipo="senha"
            autoComplete="new-password"
            maxLength={COMPRIMENTO_MAXIMO_DA_SENHA}
            aoMudar={setRepeticao}
            validar={validarRepeticao(senha)}
          />

          {problema !== null ? (
            <p role="alert" className="txt-apoio text-tinta">
              {problema}
            </p>
          ) : null}
          {recusa !== null ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
              <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
              <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
            </div>
          ) : null}

          <div className="flex items-center justify-end">
            <Botao nivel="primario" type="submit" disabled={aceitar.isPending}>
              {aceitar.isPending ? 'Gravando…' : 'Definir senha'}
            </Botao>
          </div>
        </form>
      </Bloco>
    </>
  );
}

function Recusa({ erro, comoResolver }: { readonly erro: string; readonly comoResolver: string }) {
  return (
    <Bloco titulo="Não foi possível abrir o convite">
      <div role="alert" className="flex flex-col gap-1">
        <span className="txt-corpo text-tinta">{primeiraMaiuscula(erro)}</span>
        <span className="txt-apoio text-tinta-secundaria">{comoResolver}</span>
      </div>
      <div className="flex justify-end">
        <Link to={CAMINHO_DO_LOGIN} className="botao botao-secundario">
          Voltar à entrada
        </Link>
      </div>
    </Bloco>
  );
}

function Bloco({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <h2 className="txt-secao text-tinta">{titulo}</h2>
      {children}
    </section>
  );
}
